import React from 'react';
import './PrivacyPolicy.css'; // Optional: for styling

const PrivacyPolicy: React.FC = () => {
  return (
    <div className='privacy-policy-container'>
      <h1>Privacy Policy</h1>
      <p>
        SAëF ("we," "us," or "our") is committed to protecting your privacy. This Privacy Policy
        explains how we collect, use, disclose, and safeguard your personal information when you use
        our website, mobile application, and services (collectively, the “Platform”), which connect
        customers with independent concierge providers such as security agents and other
        professional services.
      </p>
      <p>By accessing or using our Platform, you agree to the terms of this Privacy Policy.</p>

      <h2>1. Information We Collect</h2>
      <h3>a. Information You Provide Directly:</h3>
      <ul>
        <li>Name, email address, phone number</li>
        <li>Billing and payment information</li>
        <li>Government-issued identification (e.g., driver’s license) for verification</li>
        <li>Real-time facial photographs ("selfies") for identity authentication</li>
        <li>Service request details and preferences</li>
        <li>Communication and feedback</li>
      </ul>

      <h3>b. Information Collected Automatically:</h3>
      <ul>
        <li>IP address, browser type, operating system</li>
        <li>Device information, including mobile device ID</li>
        <li>Location data (if enabled)</li>
        <li>Usage logs and session activity</li>
      </ul>

      <h3>c. Information from Third Parties:</h3>
      <ul>
        <li>Identity verification vendors</li>
        <li>Payment processors</li>
        <li>Service Providers (for feedback or dispute resolution)</li>
      </ul>

      <h2>2. How We Use Your Information</h2>
      <ul>
        <li>Facilitate and manage service bookings</li>
        <li>Verify identity and prevent fraud</li>
        <li>Process payments and issue receipts</li>
        <li>Communicate updates, confirmations, and support</li>
        <li>Personalize your experience on the Platform</li>
        <li>Enforce our Terms and Conditions</li>
        <li>Comply with legal and regulatory obligations</li>
      </ul>

      <h2>3. Identity Verification and Biometric Data</h2>
      <p>
        To ensure safety and compliance, users and service providers may be asked to verify their
        identity by submitting government-issued ID and a facial photo. This data may be processed
        using third-party tools to compare and validate user identity.
      </p>
      <p>
        We do not share, sell, or use biometric data for marketing purposes. This information is
        stored securely and retained only for as long as necessary to fulfill verification and legal
        requirements.
      </p>

      <h2>4. Sharing Your Information</h2>
      <p>We may share your information with:</p>
      <ul>
        <li>Service Providers to facilitate the services you request</li>
        <li>
          Third-party vendors for payment processing, identity verification, customer support, etc.
        </li>
        <li>Law enforcement or government agencies when required by law</li>
        <li>Affiliated partners (with your consent) to provide additional services</li>
      </ul>
      <p>We do not sell or rent your personal data to third parties.</p>

      <h2>5. Your Privacy Rights</h2>
      <p>Depending on your jurisdiction, you may have rights including:</p>
      <ul>
        <li>Access to the personal data we hold about you</li>
        <li>Correction or deletion of inaccurate data</li>
        <li>Withdrawal of consent</li>
        <li>Objection to or restriction of certain data processing</li>
        <li>Data portability</li>
      </ul>
      <p>
        To exercise these rights, please contact us at{' '}
        <a href='mailto:support@saef.app'>support@saef.app</a>.
      </p>

      <h2>6. Data Retention</h2>
      <p>We retain personal information only as long as necessary for:</p>
      <ul>
        <li>The purposes described in this policy</li>
        <li>Legal or compliance requirements</li>
        <li>Dispute resolution</li>
      </ul>
      <p>
        Identity verification documents are securely deleted or anonymized after use, unless
        otherwise required by law.
      </p>

      <h2>7. Security of Your Information</h2>
      <p>
        We implement reasonable technical and organizational safeguards to protect your personal
        data, including:
      </p>
      <ul>
        <li>Secure encryption of sensitive data</li>
        <li>Restricted access to personal data</li>
        <li>Use of trusted third-party platforms for secure transactions</li>
      </ul>
      <p>However, no system can be 100% secure, and we cannot guarantee absolute data security.</p>

      <h2>8. Children’s Privacy</h2>
      <p>
        Our Platform is not intended for individuals under 18. We do not knowingly collect or store
        personal information from minors. If we learn that we have inadvertently collected such
        data, we will promptly delete it.
      </p>

      <h2>9. Changes to This Policy</h2>
      <p>
        We may update this Privacy Policy from time to time. If changes are made, we will notify you
        by revising the “Effective Date” and, where appropriate, provide additional notice.
        Continued use of our services after updates constitutes your acceptance of the revised
        policy.
      </p>

      <h2>10. Contact Us</h2>
      <p>
        If you have any questions about this Privacy Policy or your personal data, please contact us
        at <a href='mailto:support@saef.app'>support@saef.app</a>.
      </p>
    </div>
  );
};

export default PrivacyPolicy;
